import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, X, Phone, Lock, Megaphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { photoUrl } from "@/lib/api";
import { ageRangeOptions, ageRangeLabelT } from "@/lib/labels";
import { casesApi, seekersApi } from "@/lib/endpoints";
import { ShareButton } from "@/components/ShareButton";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { Aftershocks } from "@/components/Aftershocks";
import { LocationSelect } from "@/components/LocationSelect";

function CaseCard({ c, t }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden" data-testid={`child-card-${c.id}`}>
      <Link to={`/caso/${c.id}`}>
        <div className="aspect-[3/4] bg-neutral-200 relative">
          <img src={photoUrl(c.photo_path)} alt={c.first_name || "menor"} loading="lazy"
            className="w-full h-full object-cover" />
          <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-[#0066CC] text-white px-2 py-0.5 rounded-sm">{t("card.found")}</span>
        </div>
      </Link>
      <div className="p-3 flex flex-col gap-1">
        <span className="font-semibold text-neutral-900 truncate">
          {c.name_unknown ? t("card.nameUnknown") : c.first_name}
        </span>
        <span className="text-sm text-neutral-600">{ageRangeLabelT(c.age_range, t)}</span>
        {c.name_unknown && (
          <span className="text-xs text-neutral-500 line-clamp-2">
            {[c.clothing && `${t("card.clothing")}: ${c.clothing}`, c.distinguishing_marks && `${t("card.marks")}: ${c.distinguishing_marks}`].filter(Boolean).join(" · ")}
          </span>
        )}
        <span className="text-xs text-neutral-500 uppercase tracking-wide flex items-center gap-1 truncate">
          <MapPin className="w-3 h-3 shrink-0" /> {c.municipality}
        </span>
        {c.status === "localizada" ? (
          <span className="mt-1 text-xs font-semibold text-[#166534]">{t("card.familyLocated")}</span>
        ) : (
          <span className="mt-1 text-xs font-semibold text-[#B45309]">{t("card.searchingFamily")}</span>
        )}
        <Link to={`/caso/${c.id}`} data-testid={`claim-family-btn-${c.id}`}
          className="mt-2 w-full text-sm min-h-[36px] flex items-center justify-center bg-white border border-neutral-300 text-neutral-800 rounded-sm hover:bg-neutral-50">
          {t("card.isMyRelative")}
        </Link>
        <div className="mt-2">
          <ShareButton url={`/caso/${c.id}`} testid={`share-case-${c.id}`} compact
            text={t("card.shareCaseText", { name: c.name_unknown ? t("card.thisMinor") : c.first_name, muni: c.municipality })} />
        </div>
      </div>
    </div>
  );
}

function SeekerCard({ s, t }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden" data-testid={`seeker-card-${s.id}`}>
      <Link to={`/busqueda/${s.id}`}>
        <div className="aspect-[3/4] bg-neutral-200 relative">
          <img src={photoUrl(s.photo_paths[0])} alt={s.child_name} loading="lazy"
            className="w-full h-full object-cover" />
          <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-[#B45309] text-white px-2 py-0.5 rounded-sm">{t("card.search")}</span>
          {s.photo_paths.length > 1 && (
            <span className="absolute bottom-2 right-2 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded-sm">+{s.photo_paths.length - 1}</span>
          )}
        </div>
      </Link>
      <div className="p-3 flex flex-col gap-1">
        <span className="font-semibold text-neutral-900 truncate">{s.child_name}</span>
        <span className="text-sm text-neutral-600">{ageRangeLabelT(s.age_range, t)}</span>
        <span className="text-xs text-neutral-500 uppercase tracking-wide flex items-center gap-1 truncate">
          <MapPin className="w-3 h-3 shrink-0" /> {s.municipality}
        </span>
        {s.status === "localizada" ? (
          <span className="mt-1 text-xs font-semibold text-[#166534]">{t("card.reunited")}</span>
        ) : (
          <span className="mt-1 text-xs text-neutral-600 truncate">{t("card.reports")} <span className="font-medium">{s.reporter_name}</span></span>
        )}
        <div className="mt-1 flex flex-col gap-1">
          {s.phone1 && (
            <a href={`tel:${s.phone1}`} data-testid={`seeker-phone1-${s.id}`}
              className="text-sm text-[#0066CC] flex items-center gap-1 hover:underline truncate"><Phone className="w-3 h-3 shrink-0" /> {s.phone1}</a>
          )}
          {s.phone2 && (
            <a href={`tel:${s.phone2}`} className="text-sm text-[#0066CC] flex items-center gap-1 hover:underline truncate"><Phone className="w-3 h-3 shrink-0" /> {s.phone2}</a>
          )}
        </div>
        <Link to={`/busqueda/${s.id}`} data-testid={`seeker-detail-btn-${s.id}`}
          className="mt-2 w-full text-sm min-h-[36px] flex items-center justify-center bg-white border border-neutral-300 text-neutral-800 rounded-sm hover:bg-neutral-50">
          {t("card.viewDetail")}
        </Link>
        <div className="mt-2">
          <ShareButton url={`/busqueda/${s.id}`} testid={`share-seeker-${s.id}`} compact
            text={t("card.shareSeekerText", { name: s.child_name, muni: s.municipality })} />
        </div>
      </div>
    </div>
  );
}

export default function SearchPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stateName, setStateName] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | found | seeking

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (stateName) params.state = stateName;
      if (municipality) params.municipality = municipality;
      if (ageRange) params.age_range = ageRange;
      const [found, seeking] = await Promise.all([
        typeFilter === "seeking" ? Promise.resolve([]) : casesApi.list(params),
        typeFilter === "found" ? Promise.resolve([]) : seekersApi.list(params),
      ]);
      const merged = [...found, ...seeking].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      setItems(merged);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [q, stateName, municipality, ageRange, typeFilter]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const clearFilters = () => { setQ(""); setStateName(""); setMunicipality(""); setAgeRange(""); setTypeFilter("all"); };
  const hasFilters = q || stateName || municipality || ageRange || typeFilter !== "all";

  return (
    <div className="flex flex-col gap-4">
      <DisclaimerBanner />
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-neutral-900">
          {t("nav.brand")}
        </h1>
        <p className="text-base sm:text-lg font-semibold text-[#0066CC] mt-1">
          {t("search.tagline")}
        </p>
        <p className="text-sm text-neutral-600 mt-1">
          {t("search.subtitle")}
        </p>
        <p className="text-xs text-neutral-400 mt-1.5">{t("search.exclusive")}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Link to="/buscar-familiar" data-testid="seek-cta-btn"
          className="bg-[#0066CC] text-white hover:bg-[#0052A3] rounded-sm min-h-[72px] flex flex-col items-center justify-center gap-0.5 text-base font-semibold px-2 text-center">
          <span>{t("search.seekCta")}</span>
          <span className="text-xs font-normal opacity-90">{t("search.seekCtaSub")}</span>
        </Link>
        <Link to="/reportar" data-testid="report-cta-btn"
          className="bg-white border-2 border-[#0066CC] text-[#0066CC] hover:bg-blue-50 rounded-sm min-h-[72px] flex flex-col items-center justify-center gap-0.5 text-base font-semibold px-2 text-center">
          <span>{t("search.reportCta")}</span>
          <span className="text-xs font-normal opacity-90">{t("search.reportCtaSub")}</span>
        </Link>
      </div>

      <Link to="/muro" data-testid="wall-cta-btn"
        className="bg-[#166534] text-white hover:opacity-90 rounded-sm min-h-[56px] flex items-center justify-center gap-2 text-base font-semibold px-3 text-center">
        <Megaphone className="w-5 h-5" /> {t("search.wallCta")}
      </Link>

      <Aftershocks />

      <div className="flex flex-col gap-2 bg-white border border-neutral-200 rounded-sm p-3">
        <div className="grid grid-cols-3 gap-1">
          {[["all", t("search.filterAll")], ["found", t("search.filterFound")], ["seeking", t("search.filterSeeking")]].map(([v, l]) => (
            <button key={v} data-testid={`type-${v}`} onClick={() => setTypeFilter(v)}
              className={`text-sm font-semibold py-2 rounded-sm border ${typeFilter === v ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white border-neutral-300 text-neutral-700"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("search.searchPlaceholder")}
            className="w-full min-h-[48px] rounded-sm border border-neutral-300 bg-white pl-9 pr-3 text-base focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC] outline-none" />
        </div>
        <LocationSelect prefix="filter" state={stateName} municipality={municipality}
          onChange={({ state, municipality }) => { setStateName(state); setMunicipality(municipality); }} />
        <select data-testid="filter-age" className="native-select"
          value={ageRange} onChange={(e) => setAgeRange(e.target.value)}>
          <option value="">{t("search.allAges")}</option>
          {ageRangeOptions(t).map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        {hasFilters && (
          <button data-testid="clear-filters-btn" onClick={clearFilters}
            className="text-sm text-neutral-500 hover:text-[#B91C1C] flex items-center gap-1 self-start">
            <X className="w-4 h-4" /> {t("search.clearFilters")}
          </button>
        )}
      </div>

      <p className="flex items-start gap-1.5 text-xs text-neutral-500" data-testid="privacy-note">
        <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5 text-neutral-400" />
        {t("search.privacyNote")}
      </p>

      {loading ? (
        <p className="text-center text-neutral-500 py-8" data-testid="loading-state">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <div className="text-center text-neutral-500 py-12 border border-dashed border-neutral-300 rounded-sm" data-testid="empty-state">
          <p className="font-semibold">{t("search.emptyTitle")}</p>
          <p className="text-sm mt-1">{t("search.emptySub")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-testid="cases-grid">
          {items.map((it) => it.kind === "seeking"
            ? <SeekerCard key={`s-${it.id}`} s={it} t={t} />
            : <CaseCard key={`c-${it.id}`} c={it} t={t} />)}
        </div>
      )}
    </div>
  );
}
